// File: src/pages/LegalPage.jsx
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IoChevronForward,
  IoChevronDown,
  IoHelpCircleOutline,
  IoShieldCheckmarkOutline,
  IoPeopleOutline,
  IoDocumentTextOutline,
  IoBookOutline,
  IoInformationCircleOutline,
} from 'react-icons/io5';
import { LuArrowLeft } from 'react-icons/lu';
import MinimalFooter from '../components/MinimalFooter';
import { AuthContext } from '../context/AuthContext';

// ✅ Imports per your pattern
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';

// ✅ Single source of truth for user-facing legal docs
import {
  USER_TERMS_OF_SERVICE,
  USER_PRIVACY_POLICY,
  USER_COMMUNITY_GUIDELINES,
} from '../components/legal/userLegalText';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

// -----------------------------------------------
// Nested mini-accordion for the FAQ section
// -----------------------------------------------
function FaqQuestion({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <button type="button" style={FAQ.qRow} onClick={() => setOpen(!open)}>
        <span style={FAQ.qText}>{question}</span>
        {open ? <IoChevronDown style={FAQ.chev} /> : <IoChevronForward style={FAQ.chev} />}
      </button>
      {open && <div style={FAQ.aBlock}>{answer}</div>}
    </div>
  );
}

function FaqContent() {
  const faqItems = [
    {
      q: 'Where is Tribe available?',
      a: (
        <p>
          Tribe is currently available in select areas and event availability depends on what’s live in your city.
          Open the Events tab to see what’s happening near you.
        </p>
      ),
    },
    {
      q: 'Do I need to pay to use Tribe?',
      a: (
        <p>
          Creating an account is free, but most events require a ticket purchase. Pricing is shown on each event page
          before you check out.
        </p>
      ),
    },
    {
      q: 'Is it safe to meet people in person?',
      a: (
        <p>
          Tribe events are led by trained, vetted hosts who run the experience and help keep things smooth on-site.
          That said, in-person events still carry real-world risk. Use good judgment, respect boundaries, and leave
          if anything feels off. If there’s an issue, report it through Settings → Support or email us.
        </p>
      ),
    },
    {
      q: 'Can I block or report someone?',
      a: (
        <p>
          Yes. If someone’s behavior makes you uncomfortable or violates our Community Guidelines, you can report it
          through Settings → Support. We review reports and may take action up to suspension or removal.
        </p>
      ),
    },
    {
      q: 'Why do you ask for location access?',
      a: (
        <p>
          Location helps us show nearby events, support directions, and power location-based features in the event
          portal. You can use Tribe without enabling location, but some features may be limited. See our Privacy
          Policy for details.
        </p>
      ),
    },
    {
      q: 'How do I suggest a new event type or feature?',
      a: (
        <p>
          Send feedback through Settings → Support, or email us with your idea. We review suggestions and use them to
          guide future improvements.
        </p>
      ),
    },
  ];

  return (
    <div>
      {faqItems.map((item, idx) => (
        <FaqQuestion key={idx} question={item.q} answer={item.a} />
      ))}
    </div>
  );
}


// -----------------------------------------------
// Render verbatim legal doc text without changing UI
// (Uses existing R.body typography; just preserves whitespace)
// -----------------------------------------------
function LegalDoc({ text }) {
  return (
    <div style={{ whiteSpace: 'pre-wrap' }}>
      {text}
    </div>
  );
}

// -----------------------------------------------
// Sections (robust copy)
// -----------------------------------------------
const sections = [
    {
  key: 'howItWorks',
  title: 'How It Works',
  icon: <IoInformationCircleOutline size={16} />,
  content: (
    <div>
      <p>
        Tribe is built around real, host-led IRL events. Here’s the flow from start to finish.
      </p>

      <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.4' }}>
        <li>
          <strong>Host-led events:</strong> Events are run by trained Tribe hosts.
        </li>
        <li>
          <strong>Find an event:</strong> Browse the app and pick an event you want to attend.
        </li>
        <li>
          <strong>Buy a ticket:</strong> Purchase your ticket in the app to reserve your spot.
        </li>
        <li>
          <strong>Show up:</strong> Arrive at the venue at the event start time.
        </li>
        <li>
          <strong>Find your group:</strong> Use the chat field in the event portal to locate and connect with your group.
        </li>
        <li>
          <strong>Enjoy the event:</strong> Participate in the experience hosted on-site.
        </li>
        <li>
          <strong>Reconnect after:</strong> The people you met will appear on your Friends page. You can send a friend request, accept, or decline.
          If both people accept, you’ll be able to chat with each other in the app.
        </li>
      </ol>
    </div>
  ),
},

  {
    key: 'faq',
    title: 'FAQ',
    icon: <IoHelpCircleOutline size={16} />,
    content: <FaqContent />,
  },
  {
    key: 'ourStory',
    title: 'Our Story',
    icon: <IoBookOutline size={16} />,
    content: (
      <p>
        Tribe began with one simple but powerful mission: <strong>make loneliness a choice.</strong> 
        In a world overflowing with addictive digital experiences that often isolate us, we’ve set out to build 
        something different—platforms and events that bring people closer in truly meaningful ways. We believe everyone 
        should have easy, welcoming opportunities to meet others face-to-face, form genuine bonds, and foster 
        authentic community. 
        <br /><br />
        As we continue to expand, our hope is that more people discover fulfilling connections, creating a ripple 
        effect of engagement and empathy. Join us in shaping a future where no one has to be alone, and where real 
        conversation is always just a step away.
      </p>
    ),
  },
  {
    key: 'privacy',
    title: 'Privacy Policy',
    icon: <IoShieldCheckmarkOutline size={16} />,
    content: <LegalDoc text={USER_PRIVACY_POLICY} />,
  },
  {
    key: 'terms',
    title: 'Terms of Service',
    icon: <IoDocumentTextOutline size={16} />,
    content: <LegalDoc text={USER_TERMS_OF_SERVICE} />,
  },

  {
    key: 'community',
    title: 'Community Guidelines',
    icon: <IoPeopleOutline size={16} />,
    content: <LegalDoc text={USER_COMMUNITY_GUIDELINES} />,
  },
];

export default function LegalPage() {
  const { currentUser } = useContext(AuthContext);
  const isLoggedIn = Boolean(currentUser?._id);
  const [openSection, setOpenSection] = useState(null);
  const { section } = useParams();
  const navigate = useNavigate();

  // 🚨 Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  // route -> key
  useEffect(() => {
    if (!section) return;
    const map = {
      'privacy-policy': 'privacy',
      'terms-of-service': 'terms',
      'community-guidelines': 'community',
      'how-it-works': 'howItWorks',
      faq: 'faq',
      'our-story': 'ourStory',
    };
    setOpenSection(map[section] || section);
  }, [section]);

  // hide auth hairline
  useEffect(() => {
    const el = document.getElementById('authNavLine');
    if (!el) return;
    const prev = el.style.display;
    el.style.setProperty('display', 'none', 'important');
    return () => { el.style.display = prev; };
  }, []);

  const handleToggle = (k) => setOpenSection((prev) => (prev === k ? null : k));
  const handleNavBack = () => navigate(-1);

  if (!isLoggedIn) return null; // nothing rendered during redirect

  return (
    <div style={L.page}>
      {/* Topline header (logo, back, centered title, right-side tooltip) */}
      <header style={H.header}>
        <div style={H.logoRow}>
          <TribeLogo style={{ width: 72, height: 'auto' }} />
        </div>

        <div style={H.navRow}>
          <button onClick={handleNavBack} style={H.backBtn} aria-label="Back">
            <LuArrowLeft size={16} color="#fff" />
          </button>

          <div style={H.title}>Legal Information</div>

          <div style={H.rightSlot}>
            <EventHelpTooltip
              title="About Legal"
              body="Legal includes Privacy Policy, Terms of Service, Community Guidelines, How It Works, and FAQs. If you still have questions, email support@jointribe.io."
            />
          </div>
        </div>
      </header>

      {/* main scrollable content */}
      <main style={{ ...L.container, ...L.flexFill }}>
        <section style={L.list}>
          {sections.map((s) => (
            <div key={s.key} style={R.rowWrap}>
              <button
                type="button"
                style={R.row}
                onClick={() => handleToggle(s.key)}
                aria-expanded={openSection === s.key}
              >
                <div style={R.left}>
                  <div style={R.icon}>{s.icon}</div>
                  <span style={R.title}>{s.title}</span>
                </div>
                {openSection === s.key ? (
                  <IoChevronDown style={R.chev} />
                ) : (
                  <IoChevronForward style={R.chev} />
                )}
              </button>

              {openSection === s.key && (
                <div style={R.content}>
                  <div style={R.hairline} />
                  <div style={R.body}>{s.content}</div>
                </div>
              )}
              <div style={R.divider} />
            </div>
          ))}
        </section>
      </main>

      <div style={L.footerWrap}>
        <MinimalFooter />
      </div>
    </div>
  );
}

/* ───────── Layout ───────── */
const L = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100dvh',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: '#0b0b0b',
  },
  flexFill: { flex: '1 1 auto', minHeight: 0 },
  footerWrap: { marginTop: 'auto', flexShrink: 0 },
  container: {
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: `16px 16px calc(24px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  list: { display: 'flex', flexDirection: 'column' },
};

/* ───────── Header Styles ───────── */
const H = {
  header: {
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    background: '#fff',
    zIndex: 100,
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '0.5rem',
  },
  navRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    height: 44,
    padding: '0.5rem 1rem',
    marginBottom: 0,
  },
  backBtn: {
    position: 'absolute',
    left: 16,
    background: '#000',
    color: '#fff',
    borderRadius: 9999,
    width: 40,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: 'none',
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 900,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  rightSlot: {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
  },
};

const R = {
  rowWrap: { width: '100%' },
  row: {
    width: '100%',
    background: '#fff',
    border: 0,
    padding: '12px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    cursor: 'pointer',
  },
  left: { display: 'flex', alignItems: 'center', gap: 10 },
  icon: {
    width: 28, height: 28, borderRadius: 8,
    display: 'grid', placeItems: 'center',
    background: '#f3f4f6', border: '1px solid #e5e7eb',
    color: '#0b0b0b',
  },
  title: { fontSize: 14, fontWeight: 900, color: '#0b0b0b' },
  chev: { color: '#6b7280', fontSize: 18 },
  divider: { height: 1, background: '#eef0f2' },
  hairline: { height: 1, background: '#eef0f2' },
  content: { background: '#fff' },
  body: { padding: '12px 0 14px', fontSize: 14, lineHeight: 1.6, color: '#374151' },
};

const C = { list: { paddingLeft: '1.25rem', display: 'grid', gap: 6, margin: 0 } };
const FAQ = {
  qRow: {
    width: '100%', background: '#f9fafb', border: 0, borderRadius: 10,
    padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer',
  },
  qText: { fontSize: 14, fontWeight: 800 },
  chev: { fontSize: 18, color: '#6b7280' },
  aBlock: { marginTop: 8, padding: '0 2px', fontSize: 14, lineHeight: 1.6 },
};
